import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

// User registration data interface
export interface UserRegistrationData {
  user_name?: string;
  user_email?: string;
  user_password?: string;
  user_password_confirmation?: string;
}

interface UserRegistrationStepProps {
  data: UserRegistrationData;
  onChange: (data: Partial<UserRegistrationData>) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string[]>;
}

// Password strength validation
interface PasswordStrength {
  score: number;
  feedback: string[];
  isValid: boolean;
}

const validatePasswordStrength = (password: string): PasswordStrength => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('At least 8 characters');
  }

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Both uppercase and lowercase letters');
  }

  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one number');
  }

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one special character');
  }

  return {
    score,
    feedback,
    isValid: score >= 4
  };
};

/**
 * User Registration Step Component
 * Handles user account creation as the first step of unified onboarding
 */
export const UserRegistrationStep: React.FC<UserRegistrationStepProps> = ({
  data,
  onChange,
  onValidationChange,
  errors = {}
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    isValid: false
  });
  const [emailTaken, setEmailTaken] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Validation states
  const [validationStates, setValidationStates] = useState({
    name: false,
    email: false,
    password: false,
    passwordConfirmation: false
  });

  // Update validation when data changes
  useEffect(() => {
    const isNameValid = (data.user_name?.trim().length || 0) >= 2;
    const isEmailFormatValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.user_email || '');
    const isPasswordValid = passwordStrength.isValid && (data.user_password?.length || 0) >= 8;
    const isPasswordConfirmationValid = data.user_password === data.user_password_confirmation && (data.user_password_confirmation?.length || 0) > 0;

    const newValidationStates = {
      name: isNameValid,
      email: isEmailFormatValid && !emailTaken && !checkingEmail,
      password: isPasswordValid,
      passwordConfirmation: isPasswordConfirmationValid
    };

    // Only update if validation states actually changed
    const statesChanged = Object.keys(newValidationStates).some(
      key => newValidationStates[key as keyof typeof newValidationStates] !== validationStates[key as keyof typeof validationStates]
    );

    if (statesChanged) {
      setValidationStates(newValidationStates);
    }

    // For step validation, don't block on email checking if format is valid
    const isStepValid = isNameValid && 
                       isEmailFormatValid && 
                       !emailTaken && 
                       isPasswordValid && 
                       isPasswordConfirmationValid;
    
    onValidationChange(isStepValid);
  }, [data, passwordStrength, emailTaken, checkingEmail, validationStates, onValidationChange]);

  // Check password strength when password changes
  useEffect(() => {
    if (data.user_password) {
      setPasswordStrength(validatePasswordStrength(data.user_password));
    } else {
      setPasswordStrength({ score: 0, feedback: [], isValid: false });
    }
  }, [data.user_password]);

  // Handle field changes
  const handleFieldChange = useCallback((field: keyof UserRegistrationData, value: string) => {
    onChange({ [field]: value });
  }, [onChange]);

  // Check if email is already taken (mock implementation)
  const checkEmailAvailability = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingEmail(true);
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock check - in real implementation, this would call an API
    const takenEmails = ['test@example.com', 'admin@vegadigital.com'];
    setEmailTaken(takenEmails.includes(email.toLowerCase()));
    setCheckingEmail(false);
  }, []);

  // Debounced email check
  useEffect(() => {
    if (data.user_email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.user_email)) {
      const timer = setTimeout(() => {
        checkEmailAvailability(data.user_email!);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      // Reset email taken state if email is invalid or empty
      setEmailTaken(false);
    }
  }, [data.user_email]);

  const getPasswordStrengthColor = (score: number) => {
    if (score <= 1) return 'error';
    if (score === 2) return 'warning';
    if (score === 3) return 'info';
    return 'success';
  };

  const getPasswordStrengthLabel = (score: number) => {
    if (score <= 1) return 'Weak';
    if (score === 2) return 'Fair';
    if (score === 3) return 'Good';
    return 'Strong';
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
        Create Your Account
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Let's start by creating your VegaDigital account. You'll use this to access your project dashboard and communicate with your team.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Full Name */}
        <TextField
          fullWidth
          label="Full Name *"
          value={data.user_name || ''}
          onChange={(e) => handleFieldChange('user_name', e.target.value)}
          error={!!errors.user_name || (!validationStates.name && !!data.user_name)}
          helperText={
            errors.user_name?.[0] || 
            (!validationStates.name && data.user_name ? 'Name must be at least 2 characters' : '')
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color={validationStates.name ? 'success' : 'disabled'} />
              </InputAdornment>
            ),
            endAdornment: validationStates.name && (
              <InputAdornment position="end">
                <CheckIcon color="success" />
              </InputAdornment>
            )
          }}
          placeholder="Enter your full name"
          autoComplete="name"
          required
        />

        {/* Email Address */}
        <TextField
          fullWidth
          type="email"
          label="Email Address *"
          value={data.user_email || ''}
          onChange={(e) => handleFieldChange('user_email', e.target.value)}
          error={!!errors.user_email || emailTaken || (!validationStates.email && !!data.user_email && !checkingEmail)}
          helperText={
            errors.user_email?.[0] || 
            (emailTaken ? 'This email is already registered' : 
             (!validationStates.email && data.user_email ? 'Please enter a valid email address' : ''))
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color={validationStates.email ? 'success' : 'disabled'} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {checkingEmail && <CircularProgress size={20} />}
                {!checkingEmail && validationStates.email && <CheckIcon color="success" />}
                {!checkingEmail && emailTaken && <CancelIcon color="error" />}
              </InputAdornment>
            )
          }}
          placeholder="Enter your email address"
          autoComplete="email"
          required
        />

        {/* Password */}
        <Box>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Password *"
            value={data.user_password || ''}
            onChange={(e) => handleFieldChange('user_password', e.target.value)}
            error={!!errors.user_password || (!passwordStrength.isValid && !!data.user_password)}
            helperText={errors.user_password?.[0]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color={passwordStrength.isValid ? 'success' : 'disabled'} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            placeholder="Create a strong password"
            autoComplete="new-password"
            required
          />
          
          {/* Password Strength Indicator */}
          {data.user_password && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Password Strength:
                </Typography>
                <Chip
                  label={getPasswordStrengthLabel(passwordStrength.score)}
                  color={getPasswordStrengthColor(passwordStrength.score)}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={(passwordStrength.score / 4) * 100}
                color={getPasswordStrengthColor(passwordStrength.score)}
                sx={{ mb: 1 }}
              />
              {passwordStrength.feedback.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Missing: {passwordStrength.feedback.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Password Confirmation */}
        <TextField
          fullWidth
          type={showPasswordConfirmation ? 'text' : 'password'}
          label="Confirm Password *"
          value={data.user_password_confirmation || ''}
          onChange={(e) => handleFieldChange('user_password_confirmation', e.target.value)}
          error={
            !!errors.user_password_confirmation || 
            (!validationStates.passwordConfirmation && !!data.user_password_confirmation)
          }
          helperText={
            errors.user_password_confirmation?.[0] || 
            (!validationStates.passwordConfirmation && data.user_password_confirmation ? 
              'Passwords do not match' : '')
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color={validationStates.passwordConfirmation ? 'success' : 'disabled'} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  edge="end"
                  aria-label="toggle password confirmation visibility"
                >
                  {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                {validationStates.passwordConfirmation && (
                  <CheckIcon color="success" sx={{ ml: 1 }} />
                )}
              </InputAdornment>
            )
          }}
          placeholder="Confirm your password"
          autoComplete="new-password"
          required
        />
      </Box>

      {/* Security Notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Security Note:</strong> Your password will be encrypted and stored securely. 
          We recommend using a unique password that you don't use on other websites.
        </Typography>
      </Alert>
    </Box>
  );
};